import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Trash2, Filter, Edit2, Check, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Components
import Button from '../../components/Button';
import GlassPanel from '../../components/GlassPanel';
import EditQuestionModal from './EditQuestionModal';

// APIs
import { categoryApi } from '../../services/categoryApi';
import { questionApi } from '../../services/questionApi';

// Types
import { Category } from '../../types/category';
import { Question, QuestionType } from '../../types/question';
import { useTheme } from '../../context/ThemeContext';
import { getUserRole } from '../../utils/auth';

interface ViewQuestionsProps {
  onQuestionDeleted: () => void;
}

const ViewQuestions: React.FC<ViewQuestionsProps> = ({ onQuestionDeleted }) => {
  const { theme } = useTheme();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMaintainer, setIsMaintainer] = useState(false);
  
  const itemsPerPage = 8;
  const [questions, setQuestions] = useState<Question[]>([]);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMorePages, setHasMorePages] = useState(true);

  // Filters
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedDifficulties, setSelectedDifficulties] = useState<string[]>([]);
  const [selectedQuestionTypes, setSelectedQuestionTypes] = useState<string[]>([]);
  const [selectedVerificationStatus, setSelectedVerificationStatus] = useState<string[]>([]);
  const [pendingCategories, setPendingCategories] = useState<string[]>([]);
  const [pendingDifficulties, setPendingDifficulties] = useState<string[]>([]);
  const [pendingQuestionTypes, setPendingQuestionTypes] = useState<string[]>([]);
  const [pendingVerificationStatus, setPendingVerificationStatus] = useState<string[]>([]);
  const [filtersChanged, setFiltersChanged] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  // Stats
  const [totalHardQuestions, setTotalHardQuestions] = useState(0);
  const [totalMediumQuestions, setTotalMediumQuestions] = useState(0);
  const [totalEasyQuestions, setTotalEasyQuestions] = useState(0);
  const [totalFreeQuestions, setTotalFreeQuestions] = useState(0);
  const [totalPayedQuestions, setTotalPayedQuestions] = useState(0);

  // Check if user is admin or maintainer on component mount
  useEffect(() => {
    const { isAdmin: admin, isMaintainer: maintainer } = getUserRole();
    setIsAdmin(admin);
    setIsMaintainer(maintainer);
  }, []);

  // Initialize pending filters
  useEffect(() => {
    setPendingCategories(selectedCategories);
    setPendingDifficulties(selectedDifficulties);
    setPendingQuestionTypes(selectedQuestionTypes);
    setPendingVerificationStatus(selectedVerificationStatus);
  }, [selectedCategories, selectedDifficulties, selectedQuestionTypes, selectedVerificationStatus]);

  // Fetch questions with filters/pagination
  const fetchQuestions = useCallback(async (page: number, append: boolean = false) => {
    try {
      if (page === 0) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      
      const response = await questionApi.getAllQuestions(
        page,
        itemsPerPage,
        selectedCategories,
        selectedDifficulties,
        selectedQuestionTypes,
        selectedVerificationStatus
      );
      
      if (append) {
        setQuestions(prev => [...prev, ...(response.content || [])]);
      } else {
        setQuestions(response.content || []);
      }
      
      setTotalItems(response.totalElements || 0);
      setHasMorePages(page < (response.totalPages || 1) - 1);

      // Calculate stats
      if (response.content && Array.isArray(response.content)) {
        setTotalHardQuestions(response.content.filter((q: Question) => q.difficulty === 'HARD').length);
        setTotalMediumQuestions(response.content.filter((q: Question) => q.difficulty === 'MEDIUM').length);
        setTotalEasyQuestions(response.content.filter((q: Question) => q.difficulty === 'EASY').length);
        setTotalFreeQuestions(response.content.filter((q: Question) => q.questionType === QuestionType.FREE).length);
        setTotalPayedQuestions(response.content.filter((q: Question) => q.questionType === QuestionType.PAYED).length);
      }
    } catch (error) {
      console.error("Failed to fetch questions:", error);
      if (!append) {
        setQuestions([]);
      }
      setTotalItems(0);
      setHasMorePages(false);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [itemsPerPage, selectedCategories, selectedDifficulties, selectedQuestionTypes, selectedVerificationStatus]);

  // Load more questions when scrolling
  const handleScroll = useCallback(() => {
    if (!listContainerRef.current || isLoadingMore || !hasMorePages) return;

    const { scrollTop, scrollHeight, clientHeight } = listContainerRef.current;
    const scrollPosition = scrollTop + clientHeight;
    const scrollThreshold = scrollHeight - 100; // Load more when 100px from bottom
    
    if (scrollPosition >= scrollThreshold) {
      setCurrentPage(prev => prev + 1);
    }
  }, [isLoadingMore, hasMorePages]);

  // Apply scroll listener
  useEffect(() => {
    const listContainer = listContainerRef.current;
    if (listContainer) {
      listContainer.addEventListener('scroll', handleScroll);
      return () => {
        listContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  // Load more on page change
  useEffect(() => {
    if (currentPage > 0) {
      fetchQuestions(currentPage, true);
    }
  }, [currentPage, fetchQuestions]);

  // Initial fetch 
  useEffect(() => {
    fetchQuestions(0, false);
  }, [fetchQuestions]);

  useEffect(() => {
    (async () => {
      try {
        const response = await categoryApi.getAllCategories(0, 100);
        setCategories(response.content);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    })();
  }, []);

  // Apply filters handler
  const handleApplyFilters = useCallback(() => {
    setSelectedCategories(pendingCategories);
    setSelectedDifficulties(pendingDifficulties);
    setSelectedQuestionTypes(pendingQuestionTypes);
    setSelectedVerificationStatus(pendingVerificationStatus);
    setCurrentPage(0);
    setFiltersChanged(false);
    // The fetchQuestions call will happen via dependency changes in the useEffect
  }, [pendingCategories, pendingDifficulties, pendingQuestionTypes, pendingVerificationStatus]);

  const handleDeleteQuestion = useCallback(
    async (id: string) => {
      if (!window.confirm('هل أنت متأكد من حذف هذا السؤال؟')) return;
      try {
        setIsLoading(true);
        await questionApi.deleteQuestion(id);
        // Refresh questions from the first page
        setCurrentPage(0);
        fetchQuestions(0, false);
        onQuestionDeleted();
      } catch (error) {
        console.error("Failed to delete question:", error);
        alert("فشل حذف السؤال. الرجاء المحاولة مرة أخرى.");
      } finally {
        setIsLoading(false);
      }
    },
    [fetchQuestions, onQuestionDeleted]
  );

  const handleUpdateQuestion = useCallback(
    (updatedQuestion: Question) => {
      setQuestions(prevQuestions =>
        prevQuestions.map(q => {
          const qId = q.questionId || q.id;
          const updatedId = updatedQuestion.questionId || updatedQuestion.id;
          return qId === updatedId ? updatedQuestion : q;
        })
      );
      fetchQuestions(currentPage, false);
    },
    [currentPage, fetchQuestions]
  );

  const handleEditQuestion = (q: Question) => setEditingQuestion(q);
  const handleCancelEdit = () => setEditingQuestion(null);

  const handleVerifyQuestion = useCallback(
    async (id: string) => {
      try {
        setIsLoading(true);
        await questionApi.verifyQuestion(id);
        // Refresh questions from the first page
        setCurrentPage(0);
        fetchQuestions(0, false);
      } catch (error) {
        console.error("Failed to verify question:", error);
        alert("فشل اعتماد السؤال. الرجاء المحاولة مرة أخرى.");
      } finally {
        setIsLoading(false);
      }
    },
    [fetchQuestions]
  );

  const FilterSection = useMemo(() => {
    const toggleCategoryFilter = (categoryId: string) => {
      setPendingCategories(prev =>
        prev.includes(categoryId)
          ? prev.filter((id) => id !== categoryId)
          : [...prev, categoryId]
      );
      setFiltersChanged(true);
    };

    const toggleDifficultyFilter = (difficulty: string) => {
      setPendingDifficulties(prev =>
        prev.includes(difficulty)
          ? prev.filter((diff) => diff !== difficulty)
          : [...prev, difficulty]
      );
      setFiltersChanged(true);
    };

    const toggleQuestionTypeFilter = (type: string) => {
      setPendingQuestionTypes(prev =>
        prev.includes(type)
          ? prev.filter((t) => t !== type)
          : [...prev, type]
      );
      setFiltersChanged(true);
    };

    const toggleVerificationStatusFilter = (status: string) => {
      setPendingVerificationStatus(prev =>
        prev.includes(status)
          ? prev.filter((s) => s !== status)
          : [...prev, status]
      );
      setFiltersChanged(true);
    };

    const clearFilters = () => {
      setPendingCategories([]);
      setPendingDifficulties([]);
      setPendingQuestionTypes([]);
      setPendingVerificationStatus([]);
      setFiltersChanged(true);
    };

    return (
      <div className="space-y-4">
        <div>
          <h3 className={`text-sm font-semibold ${theme === 'light' ? 'text-gray-800' : 'text-gray-100'} mb-2`}>الفئات</h3>
          <div className="flex flex-wrap gap-2">
            {categories
              .filter((category) => category.categoryId)
              .map((category) => (
                <Button
                  key={category.categoryId}
                  variant={
                    pendingCategories.includes(category.categoryId!)
                      ? 'primary'
                      : 'secondary'
                  }
                  onClick={() => toggleCategoryFilter(category.categoryId!)}
                  className={`text-xs px-3 py-1 ${
                    pendingCategories.includes(category.categoryId!)
                      ? 'bg-[#3b82f6] text-white hover:bg-[#2563eb]'
                      : theme === 'light'
                        ? 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                        : 'bg-gray-700 text-gray-100 hover:bg-gray-600'
                  }`}
                  disabled={isLoading}
                >
                  {category.categoryName}
                </Button>
              ))}
          </div>
        </div>
        <div>
          <h3 className={`text-sm font-semibold ${theme === 'light' ? 'text-gray-800' : 'text-gray-100'} mb-2`}>الصعوبة</h3>
          <div className="flex flex-wrap gap-2">
            {(['EASY', 'MEDIUM', 'HARD'] as const).map((difficulty) => (
              <Button
                key={difficulty}
                variant={
                  pendingDifficulties.includes(difficulty)
                    ? 'primary'
                    : 'secondary'
                }
                onClick={() => toggleDifficultyFilter(difficulty)}
                className={`text-xs px-3 py-1 ${
                  pendingDifficulties.includes(difficulty)
                    ? 'bg-[#3b82f6] text-white hover:bg-[#2563eb]'
                    : theme === 'light'
                      ? 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                      : 'bg-gray-700 text-gray-100 hover:bg-gray-600'
                }`}
                disabled={isLoading}
              >
                {difficulty === 'EASY'
                  ? 'سهل'
                  : difficulty === 'MEDIUM'
                  ? 'متوسط'
                  : 'صعب'}
              </Button>
            ))}
          </div>
        </div>
        <div>
          <h3 className={`text-sm font-semibold ${theme === 'light' ? 'text-gray-800' : 'text-gray-100'} mb-2`}>نوع السؤال</h3>
          <div className="flex flex-wrap gap-2">
            {([QuestionType.FREE, QuestionType.PAYED] as const).map((type) => (
              <Button
                key={type}
                variant={
                  pendingQuestionTypes.includes(type)
                    ? 'primary'
                    : 'secondary'
                }
                onClick={() => toggleQuestionTypeFilter(type)}
                className={`text-xs px-3 py-1 ${
                  pendingQuestionTypes.includes(type)
                    ? 'bg-[#3b82f6] text-white hover:bg-[#2563eb]'
                    : theme === 'light'
                      ? 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                      : 'bg-gray-700 text-gray-100 hover:bg-gray-600'
                }`}
                disabled={isLoading}
              >
                {type === QuestionType.FREE
                  ? 'مجاني'
                  : 'مدفوع'}
              </Button>
            ))}
          </div>
        </div>
        <div>
          <h3 className={`text-sm font-semibold ${theme === 'light' ? 'text-gray-800' : 'text-gray-100'} mb-2`}>حالة التحقق</h3>
          <div className="flex flex-wrap gap-2">
            {(['true', 'false'] as const).map((status) => (
              <Button
                key={status}
                variant={
                  pendingVerificationStatus.includes(status)
                    ? 'primary'
                    : 'secondary'
                }
                onClick={() => toggleVerificationStatusFilter(status)}
                className={`text-xs px-3 py-1 ${
                  pendingVerificationStatus.includes(status)
                    ? 'bg-[#3b82f6] text-white hover:bg-[#2563eb]'
                    : theme === 'light'
                      ? 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                      : 'bg-gray-700 text-gray-100 hover:bg-gray-600'
                }`}
                disabled={isLoading}
              >
                {status === 'true'
                  ? 'تم التحقق'
                  : 'لم يتم التحقق'}
              </Button>
            ))}
          </div>
        </div>

        <div className={`flex justify-between pt-3 border-t ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'}`}>
          <Button
            variant="secondary"
            onClick={clearFilters}
            className={`px-3 py-1 text-sm ${theme === 'light' ? 'bg-gray-100 hover:bg-gray-200 text-gray-700' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'}`}
            disabled={isLoading || (!pendingCategories.length && !pendingDifficulties.length && !pendingQuestionTypes.length && !pendingVerificationStatus.length)}
          >
            مسح المرشحات
          </Button>
          
          <Button
            variant="primary"
            onClick={handleApplyFilters}
            className="px-3 py-1 text-sm bg-[#3b82f6] text-white hover:bg-[#2563eb] flex items-center gap-1"
            disabled={isLoading || !filtersChanged}
            icon={<Check className="w-4 h-4" />}
          >
            تطبيق المرشحات
          </Button>
        </div>
      </div>
    );
  }, [categories, pendingCategories, pendingDifficulties, pendingQuestionTypes, pendingVerificationStatus, isLoading, filtersChanged, handleApplyFilters, theme]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-4 py-8">
      {/* Statistics Panel */}
      <GlassPanel className={`p-6 ${theme === 'light' ? 'bg-white' : 'bg-gray-800'} shadow-lg rounded-lg border ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'}`}>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className={`${theme === 'light' ? 'bg-gray-100' : 'bg-gray-700'} p-4 rounded-lg border ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'}`}>
            <div className="flex items-center justify-between">
              <h3 className={`text-lg font-semibold ${theme === 'light' ? 'text-gray-800' : 'text-gray-100'}`}>الأسئلة السهلة</h3>
              <span className={`text-2xl font-bold ${theme === 'light' ? 'text-green-600' : 'text-green-400'}`}>{totalEasyQuestions}</span>
            </div>
            <p className={`text-sm ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'} mt-2`}>أسئلة مناسبة للمبتدئين</p>
          </div>
          <div className={`${theme === 'light' ? 'bg-gray-100' : 'bg-gray-700'} p-4 rounded-lg border ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'}`}>
            <div className="flex items-center justify-between">
              <h3 className={`text-lg font-semibold ${theme === 'light' ? 'text-gray-800' : 'text-gray-100'}`}>الأسئلة المتوسطة</h3>
              <span className={`text-2xl font-bold ${theme === 'light' ? 'text-yellow-600' : 'text-yellow-400'}`}>{totalMediumQuestions}</span>
            </div>
            <p className={`text-sm ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'} mt-2`}>أسئلة متوسطة الصعوبة</p>
          </div>
          <div className={`${theme === 'light' ? 'bg-gray-100' : 'bg-gray-700'} p-4 rounded-lg border ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'}`}>
            <div className="flex items-center justify-between">
              <h3 className={`text-lg font-semibold ${theme === 'light' ? 'text-gray-800' : 'text-gray-100'}`}>الأسئلة الصعبة</h3>
              <span className={`text-2xl font-bold ${theme === 'light' ? 'text-red-600' : 'text-red-400'}`}>{totalHardQuestions}</span>
            </div>
            <p className={`text-sm ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'} mt-2`}>أسئلة متقدمة للمحترفين</p>
          </div>
          <div className={`${theme === 'light' ? 'bg-gray-100' : 'bg-gray-700'} p-4 rounded-lg border ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'}`}>
            <div className="flex items-center justify-between">
              <h3 className={`text-lg font-semibold ${theme === 'light' ? 'text-gray-800' : 'text-gray-100'}`}>الأسئلة المجانية</h3>
              <span className={`text-2xl font-bold ${theme === 'light' ? 'text-blue-600' : 'text-blue-400'}`}>{totalFreeQuestions}</span>
            </div>
            <p className={`text-sm ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'} mt-2`}>أسئلة متاحة مجانا</p>
          </div>
          <div className={`${theme === 'light' ? 'bg-gray-100' : 'bg-gray-700'} p-4 rounded-lg border ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'}`}>
            <div className="flex items-center justify-between">
              <h3 className={`text-lg font-semibold ${theme === 'light' ? 'text-gray-800' : 'text-gray-100'}`}>الأسئلة المدفوعة</h3>
              <span className={`text-2xl font-bold ${theme === 'light' ? 'text-purple-600' : 'text-purple-400'}`}>{totalPayedQuestions}</span>
            </div>
            <p className={`text-sm ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'} mt-2`}>أسئلة تتطلب اشتراك</p>
          </div>
        </div>
      </GlassPanel>

      {/* Filter Panel */}
      <GlassPanel className={`p-6 ${theme === 'light' ? 'bg-white' : 'bg-gray-800'} shadow-lg rounded-lg border ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'}`}>
        <div className="flex items-center mb-4">
          <Filter className="w-5 h-5 mr-2 text-[#3b82f6]" />
          <h3 className={`text-lg font-semibold ${theme === 'light' ? 'text-gray-800' : 'text-gray-100'}`}>المرشحات</h3>
        </div>
        {FilterSection}
      </GlassPanel>

      {/* Questions List Panel */}
      <GlassPanel className={`p-6 ${theme === 'light' ? 'bg-white' : 'bg-gray-800'} shadow-lg rounded-lg border ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'}`}>
        <div className="flex justify-between items-center mb-6">
          <h2 className={`text-xl font-bold ${theme === 'light' ? 'text-gray-800' : 'text-gray-100'}`}>
            الأسئلة ({totalItems})
          </h2>
        </div>
        {isLoading ? (
          <div className="flex justify-center p-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3b82f6]" />
          </div>
        ) : (
          <div 
            ref={listContainerRef}
            className="space-y-4 max-h-[70vh] overflow-y-auto pr-2 scroll-smooth"
          >
            <AnimatePresence>
              {questions.map((q, index) => (
                <motion.div
                  key={`${q.questionId || q.id}-${index}`}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="mb-4"
                >
                  <div className={`${theme === 'light' ? 'bg-white' : 'bg-gray-800'} rounded-lg shadow-lg p-4 relative transition-all duration-300 hover:shadow-xl border ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'}`}>
                    <div className="flex flex-col gap-4">
                      {/* Header */}
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            q.difficulty === 'EASY' 
                              ? theme === 'light' ? 'bg-green-100 text-green-700' : 'bg-green-900 text-green-300'
                              : q.difficulty === 'MEDIUM'
                              ? theme === 'light' ? 'bg-yellow-100 text-yellow-700' : 'bg-yellow-900 text-yellow-300'
                              : theme === 'light' ? 'bg-red-100 text-red-700' : 'bg-red-900 text-red-300'
                          }`}>
                            {q.difficulty === 'EASY'
                              ? 'سهل'
                              : q.difficulty === 'MEDIUM'
                              ? 'متوسط'
                              : 'صعب'}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            q.questionType === QuestionType.FREE 
                              ? theme === 'light' ? 'bg-blue-100 text-blue-700' : 'bg-blue-900 text-blue-300'
                              : theme === 'light' ? 'bg-purple-100 text-purple-700' : 'bg-purple-900 text-purple-300'
                          }`}>
                            {q.questionType === QuestionType.FREE
                              ? 'مجاني'
                              : 'مدفوع'}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            q.verified 
                              ? theme === 'light' ? 'bg-green-100 text-green-700' : 'bg-green-900 text-green-300'
                              : theme === 'light' ? 'bg-gray-100 text-gray-700' : 'bg-gray-900 text-gray-300'
                          }`}>
                            {q.verified
                              ? 'تم التحقق'
                              : 'لم يتم التحقق'}
                          </span>
                          <span className={`text-sm ${theme === 'light' ? 'text-gray-800' : 'text-gray-200'}`}>
                            {categories.find(c => c.categoryId === q.category)?.categoryName}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          {isAdmin && !q.verified && (
                            <Button
                              variant="secondary"
                              onClick={() => handleVerifyQuestion(q.questionId || q.id || '')}
                              className={`p-1 ${
                                theme === 'light' 
                                  ? 'text-green-500 hover:text-green-600 bg-gray-100 hover:bg-gray-200' 
                                  : 'text-green-400 bg-gray-700 hover:bg-gray-600'
                              }`}
                              icon={<CheckCircle className="w-4 h-4" />}
                            >
                              اعتماد
                            </Button>
                          )}
                          {(isAdmin || isMaintainer) && (
                            <Button
                              variant="secondary"
                              onClick={() => handleEditQuestion(q)}
                              className={`p-1 text-[#3b82f6] hover:text-[#2563eb] ${
                                theme === 'light' 
                                  ? 'bg-gray-100 hover:bg-gray-200' 
                                  : 'bg-gray-700 hover:bg-gray-600 text-blue-400'
                              }`}
                              icon={<Edit2 className="w-4 h-4" />}
                            >
                              <span className="sr-only">تعديل</span>
                            </Button>
                          )}
                          {isAdmin && (
                            <Button
                              variant="secondary"
                              onClick={() => handleDeleteQuestion(q.questionId || q.id || '')}
                              className={`p-1 ${
                                theme === 'light'
                                  ? 'text-red-500 hover:text-red-600 bg-gray-100 hover:bg-gray-200'
                                  : 'text-red-400 bg-gray-700 hover:bg-gray-600'
                              }`}
                              icon={<Trash2 className="w-4 h-4" />}
                            >
                              <span className="sr-only">حذف</span>
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Question Content */}
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <p className="text-sm font-semibold text-[#3b82f6] mb-2">السؤال</p>
                          <p className={`text-sm ${theme === 'light' ? 'text-gray-800' : 'text-gray-200'} ${theme === 'light' ? 'bg-gray-100' : 'bg-gray-700'} p-3 rounded-lg break-words`}>
                            {q.question || 'لم يتم تقديم نص السؤال'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-[#3b82f6] mb-2">الإجابة</p>
                          <p className={`text-sm ${theme === 'light' ? 'text-gray-800' : 'text-gray-200'} ${theme === 'light' ? 'bg-gray-100' : 'bg-gray-700'} p-3 rounded-lg break-words`}>
                            {q.answer || 'لم يتم تقديم إجابة'}
                          </p>
                          {q.answerRange !== undefined && q.answerRange > 0 && (
                            <div className="mt-2">
                              <span className={`text-xs font-medium px-3 py-1 rounded-full ${
                                theme === 'light' 
                                  ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                                  : 'bg-blue-900/30 text-blue-300 border border-blue-800'
                              }`}>
                                نطاق الخطأ المسموح به: ±{q.answerRange}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Images */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          {q.questionThumbnailUrl ? (
                            <div className={`relative rounded-lg overflow-hidden border ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'} shadow-sm`}>
                              <img
                                src={q.questionThumbnailUrl}
                                alt="السؤال"
                                className="w-full h-32 object-cover"
                                loading="lazy"
                              />
                            </div>
                          ) : (
                            <div className={`w-full h-32 ${theme === 'light' ? 'bg-gray-100' : 'bg-gray-700'} rounded-lg flex items-center justify-center ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'} text-sm border ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'}`}>
                              لا توجد صورة للسؤال
                            </div>
                          )}
                        </div>
                        <div>
                          {q.answerThumbnailUrl ? (
                            <div className={`relative rounded-lg overflow-hidden border ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'} shadow-sm`}>
                              <img
                                src={q.answerThumbnailUrl}
                                alt="الإجابة"
                                className="w-full h-32 object-cover"
                                loading="lazy"
                              />
                            </div>
                          ) : (
                            <div className={`w-full h-32 ${theme === 'light' ? 'bg-gray-100' : 'bg-gray-700'} rounded-lg flex items-center justify-center ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'} text-sm border ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'}`}>
                              لا توجد صورة للإجابة
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
            
            {questions.length === 0 && !isLoading && (
              <div className={`text-center p-8 ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}`}>
                لا توجد أسئلة تطابق المعايير المحددة
              </div>
            )}
            
            {isLoadingMore && (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3b82f6]" />
              </div>
            )}
            
            {!hasMorePages && questions.length > 0 && (
              <div className={`text-center py-4 ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'} text-sm`}>
                تم عرض جميع الأسئلة
              </div>
            )}
          </div>
        )}
      </GlassPanel>

      {/* Edit Question Modal */}
      {editingQuestion && (isAdmin || isMaintainer) && (
        <EditQuestionModal
          question={editingQuestion}
          onUpdate={handleUpdateQuestion}
          onCancel={handleCancelEdit}
        />
      )}
    </div>
  );
};

export default ViewQuestions;
